import React, { useEffect, useRef } from 'react';

import { useAxios } from '../../../AxiosHandler';

import { useTranslate } from 'react-polyglot';
import { useNavigate, useParams } from 'react-router-dom';

import styles from './EditPuppyListing.module.scss';
import { Form } from 'react-bootstrap';
import Card from '../../Shared/Card/Card';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function EditPuppyListing(props: EditPuppyListingProps) {
    const t = useTranslate();
    const params = useParams();
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;
    const navigate = useNavigate();

    const [loading, setLoading] = React.useState<boolean>(true);
    const [listingData, setListingData] = React.useState<any>(null);
    const [status, setStatus] = React.useState<string>("");
    const [expectingMonth, setExpectingMonth] = React.useState<string>("");
    const [expectingYear, setExpectingYear] = React.useState<string>("");
    const [birthdate, setBirthdate] = React.useState<string>("");
    const [pupsBornMale, setPupsBornMale] = React.useState<string>("");
    const [pupsBornFemale, setPupsBornFemale] = React.useState<string>("");
    const [freetext, setFreetext] = React.useState<string>("");
    const [ownerName, setOwnerName] = React.useState<string>("");
    const [kennelName, setKennelName] = React.useState<string>("");
    const [phone, setPhone] = React.useState<string>("");
    const [email, setEmail] = React.useState<string>("");
    const [postalCode, setPostalCode] = React.useState<string>("");
    const [postalArea, setPostalArea] = React.useState<string>("");
    const [publishedPublic, setPublishedPublic] = React.useState<string>("0");
    const [isDirty, setIsDirty] = React.useState<boolean>(false);
    const [damPhotoKey, setDamPhotoKey] = React.useState<number>(0);
    const [sirePhotoKey, setSirePhotoKey] = React.useState<number>(0);
    const [uploadingPhoto, setUploadingPhoto] = React.useState<'dam' | 'sire' | null>(null);
    const damFileInputRef = useRef<HTMLInputElement>(null);
    const sireFileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        fetchPuppyListing();
    }, [params.guid]);

    function getMonthName(monthNumber: string): string {
        const months: { [key: string]: string } = {
            '01': t('editpuppylisting.form.months.january'),
            '02': t('editpuppylisting.form.months.february'),
            '03': t('editpuppylisting.form.months.march'),
            '04': t('editpuppylisting.form.months.april'),
            '05': t('editpuppylisting.form.months.may'),
            '06': t('editpuppylisting.form.months.june'),
            '07': t('editpuppylisting.form.months.july'),
            '08': t('editpuppylisting.form.months.august'),
            '09': t('editpuppylisting.form.months.september'),
            '10': t('editpuppylisting.form.months.october'),
            '11': t('editpuppylisting.form.months.november'),
            '12': t('editpuppylisting.form.months.december')
        };
        return months[monthNumber] || '';
    }

    function formatExpectingDate(date: string): string {
        if (!date) return '';
        const parts = date.split('.');
        if (parts.length === 2) {
            const monthName = getMonthName(parts[0]);
            return `${monthName} ${parts[1]}`;
        }
        return date;
    }

    function uploadPhoto(file: File, dogType: 'dam' | 'sire') {
        setUploadingPhoto(dogType);
        const img = new Image();
        const reader = new FileReader();
        reader.onload = (ev) => {
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = 300;
                canvas.height = 300;
                const ctx = canvas.getContext('2d')!;
                const size = Math.min(img.width, img.height);
                const sx = (img.width - size) / 2;
                const sy = (img.height - size) / 2;
                ctx.drawImage(img, sx, sy, size, size, 0, 0, 300, 300);
                const base64 = canvas.toDataURL('image/jpeg', 0.85);
                axios.single({
                    method: 'post',
                    url: `/api/dogs/puppylisting/photo/byguid/${params.guid}`,
                    data: { photo: base64, dogType: dogType },
                    responseType: 'json'
                }).then(() => {
                    if (dogType === 'dam') setDamPhotoKey(k => k + 1);
                    else setSirePhotoKey(k => k + 1);
                }).catch((err: any) => {
                    console.log(err);
                    toast.error(t('editpuppylisting.photo.uploaderror'), {
                        position: "top-center",
                        autoClose: 5000,
                    });
                }).finally(() => {
                    setUploadingPhoto(null);
                });
            };
            img.src = ev.target?.result as string;
        };
        reader.readAsDataURL(file);
    }

    function fetchPuppyListing() {
        setLoading(true);
        axios.single({
            method: 'get',
            url: `/api/dogs/puppylisting/byguid/${params.guid}`,
            responseType: 'json'
        }).then((res: any) => {
            if (res.status !== 200 || !res.data) {
                toast.error(t('editpuppylisting.fetch.error'), {
                    position: "top-center",
                    autoClose: 5000,
                });
                setLoading(false);
                return;
            }

            const data = res.data;
            setListingData(data);
            setStatus(data.STATUS || "awaiting");
            
            // Parse EXPECTING_DATE (format: MM.YYYY)
            if (data.EXPECTING_DATE) {
                const parts = data.EXPECTING_DATE.split('.');
                setExpectingMonth(parts[0] || "");
                setExpectingYear(parts[1] || "");
            } else {
                setExpectingMonth("");
                setExpectingYear("");
            }
            
            setBirthdate(data.BIRTHDATE || "");
            
            // Parse PUPSBORN field (format: "males|females")
            if (data.PUPSBORN) {
                const parts = data.PUPSBORN.split('|');
                setPupsBornMale(parts[0] || "");
                setPupsBornFemale(parts[1] || "");
            }
            
            setFreetext(data.FREETEXT || "");
            setOwnerName(data.OWNER_NAME || "");
            setKennelName(data.KENNEL_NAME || "");
            setPhone(data.PHONE || "");
            setEmail(data.EMAIL || "");
            setPostalCode(data.POSTAL_CODE || "");
            setPostalArea(data.POSTAL_AREA || "");
            setPublishedPublic(data.PUBLISHED_PUBLIC ?? "0");
            setIsDirty(false);
            
            setLoading(false);
        }).catch((err: any) => {
            console.log(err);
            toast.error(t('editpuppylisting.fetch.error'), {
                position: "top-center",
                autoClose: 5000,
            });
            setLoading(false);
        });
    }

    function updateListing() {
        // Combine pupsBorn into single field
        const pupsBorn = pupsBornMale || pupsBornFemale ? `${pupsBornMale || "0"}|${pupsBornFemale || "0"}` : "";
        
        // Combine expectingMonth and expectingYear into MM.YYYY format
        const expectingDate = expectingMonth && expectingYear ? `${expectingMonth}.${expectingYear}` : "";

        axios.single({
            method: 'put',
            url: `/api/dogs/puppylisting/byguid/${params.guid}`,
            data: {
                status: status,
                expectingDate: expectingDate || null,
                birthdate: birthdate || null,
                pupsBorn: pupsBorn || null,
                freetext: freetext || null,
                ownerName: ownerName || null,
                kennelName: kennelName || null,
                phone: phone || null,
                email: email || null,
                postalCode: postalCode || null,
                postalArea: postalArea || null,
                publishedPublic: publishedPublic
            },
            responseType: 'json'
        }).then((res: any) => {
            if (res.status !== 200) {
                toast.error(t('editpuppylisting.form.updateerror'), {
                    position: "top-center",
                    autoClose: 5000,
                });
                return;
            }
            toast.success(t('editpuppylisting.form.success'), {
                position: "top-center",
                autoClose: 5000,
            });
            setIsDirty(false);
            // Refresh data
            fetchPuppyListing();
        }).catch((err: any) => {
            console.log(err);
            toast.error(t('editpuppylisting.form.updateerror'), {
                position: "top-center",
                autoClose: 5000,
            });
        });
    }
    
    if (loading) {
        return (
            <div className={styles.editpuppylisting}>
                <h1>{t('editpuppylisting.loading')}</h1>
            </div>
        );
    }

    if (!listingData) {
        return (
            <div className={styles.editpuppylisting}>
                <h1>{t('editpuppylisting.notfound')}</h1>
                <p>{t('editpuppylisting.notfounddesc')}</p>
            </div>
        );
    }

    return (
        <div className={styles.editpuppylisting}>
            <h1>{t('editpuppylisting.title')}</h1>
            <p>{t('editpuppylisting.description')}</p>

            <div className={styles.dogcards}>
                <div className={styles.dogcardwrapper}>
                    <div
                        className={`${styles.dogavatar} ${uploadingPhoto === 'dam' ? '' : styles.dogavatarClickable}`}
                        onClick={() => { if (!uploadingPhoto) damFileInputRef.current?.click(); }}
                        title={t('editpuppylisting.photo.change')}
                    >
                        <span className={styles.dogavatarplaceholder}>
                            {(listingData.DAM_NAME ?? "?")[0]}
                        </span>
                        {listingData.DAM_ANIMAL_ID && (
                            <img
                                key={damPhotoKey}
                                src={`/api/dogs/photo/${listingData.DAM_ANIMAL_ID}?v=${damPhotoKey}`}
                                alt=""
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                        )}
                        {uploadingPhoto === 'dam' ? (
                            <div className={`${styles.dogavataroverlay} ${styles.dogavatarOverlayVisible}`}>
                                <div className={styles.photoSpinner} />
                            </div>
                        ) : (
                            <div className={styles.dogavataroverlay}>
                                <span>&#9998;</span>
                            </div>
                        )}
                    </div>
                    <input
                        ref={damFileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) uploadPhoto(file, 'dam');
                            e.target.value = '';
                        }}
                    />
                    <Card title={t('editpuppylisting.dam')} children={
                        <div>
                            <hr/>
                            <div className={styles.dogname}>{listingData.DAM_NAME}</div>
                            {listingData.DAM_REGNO && (
                                <div className={styles.dogregno}>{listingData.DAM_REGNO}</div>
                            )}
                        </div>
                    } />
                </div>
                <div className={styles.dogcardwrapper}>
                    <div
                        className={`${styles.dogavatar} ${uploadingPhoto === 'sire' ? '' : styles.dogavatarClickable}`}
                        onClick={() => { if (!uploadingPhoto) sireFileInputRef.current?.click(); }}
                        title={t('editpuppylisting.photo.change')}
                    >
                        <span className={styles.dogavatarplaceholder}>
                            {(listingData.SIRE_NAME ?? "?")[0]}
                        </span>
                        {listingData.SIRE_ANIMAL_ID && (
                            <img
                                key={sirePhotoKey}
                                src={`/api/dogs/photo/${listingData.SIRE_ANIMAL_ID}?v=${sirePhotoKey}`}
                                alt=""
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                        )}
                        {uploadingPhoto === 'sire' ? (
                            <div className={`${styles.dogavataroverlay} ${styles.dogavatarOverlayVisible}`}>
                                <div className={styles.photoSpinner} />
                            </div>
                        ) : (
                            <div className={styles.dogavataroverlay}>
                                <span>&#9998;</span>
                            </div>
                        )}
                    </div>
                    <input
                        ref={sireFileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) uploadPhoto(file, 'sire');
                            e.target.value = '';
                        }}
                    />
                    <Card title={t('editpuppylisting.sire')} children={
                        <div>
                            <hr/>
                            <div className={styles.dogname}>{listingData.SIRE_NAME}</div>
                            {listingData.SIRE_REGNO && (
                                <div className={styles.dogregno}>{listingData.SIRE_REGNO}</div>
                            )}
                        </div>
                    } />
                </div>
            </div>

            <div className={styles.editform}>
                {isDirty && (
                    <div className={styles.unsavedBanner}>
                        <span className={styles.unsavedBannerIcon}>●</span>
                        {t('editpuppylisting.form.unsavedchanges')}
                    </div>
                )}

                <Form className={styles.form} onChange={() => setIsDirty(true)}>
                    <h3>{t('editpuppylisting.publicinfo')}</h3>

                    <div className={`${styles.publicToggleCard} ${publishedPublic === "1" ? styles.publicToggleCardPublished : ""}`}>
                        <div className={styles.publicToggleInfo}>
                            <div className={styles.publicToggleStatus}>
                                <span className={`${styles.publicToggleDot} ${publishedPublic === "1" ? styles.publicToggleDotPublished : ""}`} />
                                <strong>
                                    {publishedPublic === "1"
                                        ? t('editpuppylisting.form.publishedpublic.true')
                                        : t('editpuppylisting.form.publishedpublic.false')
                                    }
                                </strong>
                            </div>
                            <p className={styles.publicToggleHelp}>
                                {t('editpuppylisting.form.publishedpublic.help')}
                            </p>
                        </div>
                        <button
                            type="button"
                            className={`btn ${publishedPublic === "1" ? "btn-outline-danger" : "btn-success"} ${styles.publicToggleBtn}`}
                            onClick={() => { setPublishedPublic(publishedPublic === "1" ? "0" : "1"); setIsDirty(true); }}
                        >
                            {publishedPublic === "1"
                                ? t('editpuppylisting.form.publishedpublic.unpublish')
                                : t('editpuppylisting.form.publishedpublic.publish')
                            }
                        </button>
                    </div>

                    <h3>{t('editpuppylisting.statusinfo')}</h3>
                    
                    <Form.Group controlId="formStatus" className="mb-3">
                        <Form.Label>{t('editpuppylisting.form.status')}</Form.Label>
                        <Form.Select 
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                        >
                            <option value="awaiting">{t('editpuppylisting.status.awaiting')}</option>
                            <option value="mated">{t('editpuppylisting.status.mated')}</option>
                            <option value="attempted">{t('editpuppylisting.status.attempted')}</option>
                            <option value="failed">{t('editpuppylisting.status.failed')}</option>
                            <option value="successful">{t('editpuppylisting.status.successful')}</option>
                        </Form.Select>
                    </Form.Group>

                    <div className={styles.expectingDateGroup}>
                        <Form.Group controlId="formExpectingMonth" className="mb-3">
                            <Form.Label>{t('editpuppylisting.form.expectingdate')}</Form.Label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <Form.Select 
                                    value={expectingMonth}
                                    onChange={(e) => setExpectingMonth(e.target.value)}
                                    style={{ flex: 1 }}
                                >
                                    <option value="">{t('editpuppylisting.form.month')}</option>
                                    <option value="01">{t('editpuppylisting.form.months.january')}</option>
                                    <option value="02">{t('editpuppylisting.form.months.february')}</option>
                                    <option value="03">{t('editpuppylisting.form.months.march')}</option>
                                    <option value="04">{t('editpuppylisting.form.months.april')}</option>
                                    <option value="05">{t('editpuppylisting.form.months.may')}</option>
                                    <option value="06">{t('editpuppylisting.form.months.june')}</option>
                                    <option value="07">{t('editpuppylisting.form.months.july')}</option>
                                    <option value="08">{t('editpuppylisting.form.months.august')}</option>
                                    <option value="09">{t('editpuppylisting.form.months.september')}</option>
                                    <option value="10">{t('editpuppylisting.form.months.october')}</option>
                                    <option value="11">{t('editpuppylisting.form.months.november')}</option>
                                    <option value="12">{t('editpuppylisting.form.months.december')}</option>
                                </Form.Select>
                                <Form.Select 
                                    value={expectingYear}
                                    onChange={(e) => setExpectingYear(e.target.value)}
                                    style={{ flex: 1 }}
                                >
                                    <option value="">{t('editpuppylisting.form.year')}</option>
                                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i).map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </Form.Select>
                            </div>
                            <Form.Text className="text-muted">
                                {t('editpuppylisting.form.expectingdatehelp')}
                            </Form.Text>
                        </Form.Group>
                    </div>

                    <Form.Group controlId="formBirthdate" className="mb-3">
                        <Form.Label>{t('editpuppylisting.form.birthdate')}</Form.Label>
                        <Form.Control 
                            type="text" 
                            value={birthdate}
                            onChange={(e) => setBirthdate(e.target.value)}
                            placeholder={t('editpuppylisting.form.birthdateplaceholder')}
                        />
                    </Form.Group>

                    <div className={styles.pupsBornGroup}>
                        <Form.Group controlId="formPupsBornMale" className="mb-3">
                            <Form.Label>{t('editpuppylisting.form.pupsbornmale')}</Form.Label>
                            <Form.Control 
                                type="number" 
                                min="0"
                                value={pupsBornMale}
                                onChange={(e) => setPupsBornMale(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group controlId="formPupsBornFemale" className="mb-3">
                            <Form.Label>{t('editpuppylisting.form.pupsbornfemale')}</Form.Label>
                            <Form.Control 
                                type="number" 
                                min="0"
                                value={pupsBornFemale}
                                onChange={(e) => setPupsBornFemale(e.target.value)}
                            />
                        </Form.Group>
                    </div>

                    <Form.Group controlId="formFreetext" className="mb-4">
                        <Form.Label>{t('editpuppylisting.form.freetext')}</Form.Label>
                        <Form.Control 
                            as="textarea"
                            rows={4}
                            value={freetext}
                            onChange={(e) => setFreetext(e.target.value)}
                            placeholder={t('editpuppylisting.form.freetextplaceholder')}
                        />
                    </Form.Group>

                    <h3>{t('editpuppylisting.contactinfo')}</h3>

                    <Form.Group controlId="formOwnerName" className="mb-3">
                        <Form.Label>{t('editpuppylisting.form.ownername')}</Form.Label>
                        <Form.Control 
                            type="text" 
                            value={ownerName}
                            onChange={(e) => setOwnerName(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group controlId="formKennelName" className="mb-3">
                        <Form.Label>{t('editpuppylisting.form.kennelname')}</Form.Label>
                        <Form.Control 
                            type="text" 
                            value={kennelName}
                            onChange={(e) => setKennelName(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group controlId="formPhone" className="mb-3">
                        <Form.Label>{t('editpuppylisting.form.phone')}</Form.Label>
                        <Form.Control 
                            type="tel" 
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group controlId="formEmail" className="mb-3">
                        <Form.Label>{t('editpuppylisting.form.email')}</Form.Label>
                        <Form.Control 
                            type="email" 
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled
                        />
                    </Form.Group>

                    <Form.Group controlId="formPostalCode" className="mb-3">
                        <Form.Label>{t('editpuppylisting.form.postalcode')}</Form.Label>
                        <Form.Control 
                            type="text" 
                            value={postalCode}
                            onChange={(e) => setPostalCode(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group controlId="formPostalArea" className="mb-4">
                        <Form.Label>{t('editpuppylisting.form.postalarea')}</Form.Label>
                        <Form.Control 
                            type="text" 
                            value={postalArea}
                            onChange={(e) => setPostalArea(e.target.value)}
                        />
                    </Form.Group>
                </Form>

                {isDirty && (
                    <div className={styles.unsavedBanner}>
                        <span className={styles.unsavedBannerIcon}>●</span>
                        {t('editpuppylisting.form.unsavedchanges')}
                    </div>
                )}

                <button
                    className={`btn btn-primary ${styles.submitButton} ${isDirty ? styles.submitButtonDirty : ""}`}
                    onClick={(e) => {
                        e.preventDefault();
                        updateListing();
                    }}
                >
                    {t('editpuppylisting.form.submit')}
                </button>
            </div>
        </div>
    );
}

class EditPuppyListingProps {

}

export default EditPuppyListing;
